"use client"

import { useEffect, useRef, useState } from "react"
import type { Editor } from "@tiptap/react"
import { MarkButton } from "@/components/tiptap-ui/mark-button"
import { ListDropdownMenu } from "@/components/tiptap-ui/list-dropdown-menu"

interface Props {
  editor: Editor | null;
}

interface Coords {
  top: number;
  centerX: number;
}

/**
 * Floating toolbar that appears above a text selection in the editor.
 * Renders inline mark buttons (bold, italic, underline, strike, code)
 * and a list type switcher. Uses the browser Selection API for
 * positioning so it works inside Radix Dialog without portals.
 */
export function SelectionToolbar({ editor }: Props) {
  const [coords, setCoords] = useState<Coords | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editor) return;

    const handleUpdate = (): void => {
      if (editor.state.selection.empty) {
        setCoords(null);
        return;
      }

      const domSel = window.getSelection();
      if (!domSel || domSel.rangeCount === 0) {
        setCoords(null);
        return;
      }

      const rect = domSel.getRangeAt(0).getBoundingClientRect();
      if (!rect.width) {
        setCoords(null);
        return;
      }

      setCoords({
        top: rect.top,
        centerX: rect.left + rect.width / 2,
      });
    };

    const handleBlur = (): void => setCoords(null);

    editor.on("selectionUpdate", handleUpdate);
    editor.on("blur", handleBlur);

    return () => {
      editor.off("selectionUpdate", handleUpdate);
      editor.off("blur", handleBlur);
    };
  }, [editor]);

  if (!coords) return null;

  return (
    <div
      ref={toolbarRef}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.centerX,
        // Center horizontally over the selection, shift up above it
        transform: "translate(-50%, calc(-100% - 8px))",
        zIndex: 9999,
      }}
      // Prevent editor blur when interacting with the toolbar
      onMouseDown={(e) => e.preventDefault()}
      className="flex items-center gap-0.5 rounded-lg border border-border bg-popover p-1 shadow-md"
    >
      <MarkButton type="bold" />
      <MarkButton type="italic" />
      <MarkButton type="underline" hideWhenUnavailable />
      <MarkButton type="strike" />
      <MarkButton type="code" />
      <div className="mx-1 h-4 w-px bg-border" />
      <ListDropdownMenu
        types={["bulletList", "orderedList"]}
        portal
      />
    </div>
  );
}
